import Assessment from '@app/src/types/assessment'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { useIntl } from 'react-intl'

export const useAssessmentMetrics = (assessment: Assessment) => {
  const { formatMessage } = useIntl()

  const getLevelDescription = (level: number) => {
    switch (level) {
      case 1:
        return assessment.assessmentTemplate.level1Description
      case 2:
        return assessment.assessmentTemplate.level2Description
      case 3:
        return assessment.assessmentTemplate.level3Description
      case 4:
        return assessment.assessmentTemplate.level4Description
      case 5:
        return assessment.assessmentTemplate.level5Description
      default:
        return ''
    }
  }

  const getCircleColor = () => {
    const percentage = assessment.percentage ?? 0

    if (assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment) {
      if (assessment.totalLevel === 1) return '#9E0202'
      if (assessment.totalLevel === 2) return '#FA7A5D'
      if (assessment.totalLevel === 3) return '#FFC774'
      if (assessment.totalLevel === 4) return '#97DAAE'
      if (assessment.totalLevel === 5) return '#4F8D73'
    } else {
      if (percentage <= 15) return '#9E0202'
      if (percentage <= 40) return '#FA7A5D'
      if (percentage <= 50) return '#FFC774'
      if (percentage <= 70) return '#97DAAE'
      if (percentage <= 100) return '#4F8D73'
    }
    return '#4F8D73'
  }

  const getCircleValue = () => {
    const percentage = assessment.percentage

    if (assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment) {
      return (assessment.totalLevel ?? 0) * 20
    }
    return percentage
  }

  const getStatusText = () => {
    if (assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment) {
      return formatMessage({ id: 'assessment.level' }, { level: assessment.totalLevel })
    }
    return assessment.totalLevel === 1
      ? formatMessage({ id: 'assessment.status.poor' })
      : assessment.totalLevel === 2
        ? formatMessage({ id: 'assessment.status.fair' })
        : assessment.totalLevel === 3
          ? formatMessage({ id: 'assessment.status.good' })
          : assessment.totalLevel === 4
            ? formatMessage({ id: 'assessment.status.veryGood' })
            : formatMessage({ id: 'assessment.status.excellent' })
  }

  const getScoreText = () => {
    if (assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment) {
      return formatMessage(
        { id: 'assessment.scoreText.baseline' },
        { status: getStatusText(), score: assessment.totalScore },
      )
    }
    return formatMessage(
      { id: 'assessment.scoreText.standard' },
      { status: getStatusText(), percentage: assessment.percentage },
    )
  }

  const getCircleText = () => {
    if (assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment) {
      return formatMessage({ id: 'assessment.levelShort' }, { level: assessment.totalLevel })
    }
    return assessment.percentage
  }

  return {
    getLevelDescription,
    getCircleColor,
    getCircleValue,
    getStatusText,
    getScoreText,
    getCircleText,
  }
}
